import React, { useEffect, useState } from 'react';
import { Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import {
    SegmentIncomeStatementResponse,
    SegmentIncomeStatementBlock,
    SegmentReportLine,
    Segment,
} from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const formatAmount = (value: number) =>
    value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = new Date();
const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
const todayStr = today.toISOString().split('T')[0];

const SegmentIncomeStatement: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<SegmentIncomeStatementResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [from, setFrom] = useState(firstOfMonth);
    const [to, setTo] = useState(todayStr);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>('');

    useEffect(() => {
        const loadSegments = async () => {
            try {
                const page = await SegmentService.getByInstitution();
                setSegments(page.content ?? []);
            } catch (err) {
                toast.showApiError(err, 'Failed to load segments');
            }
        };
        loadSegments();
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const segmentId = selectedSegmentId || undefined;
            const data = await AccountService.getIncomeStatement(from, to, segmentId);
            setReport(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to load segment income statement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasData = report && report.segments.length > 0;

    const netProfitColor = (value: number) => (value >= 0 ? '#2e7d32' : '#c62828');

    const renderLineTable = (lines: SegmentReportLine[]) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
            <thead>
                <tr>
                    <th style={tableHeaderStyle}>Code</th>
                    <th style={tableHeaderStyle}>Account Name</th>
                    <th style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Amount</th>
                </tr>
            </thead>
            <tbody>
                {lines.map((line, i) => (
                    <tr key={line.accountId} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={tableCellStyle}>{line.accountCode}</td>
                        <td style={tableCellStyle}>{line.accountName}</td>
                        <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.netBalance)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Segment Income Statement</h2>
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <FormControl size="small" style={{ minWidth: 200 }}>
                    <InputLabel id="segment-filter-label">Segment</InputLabel>
                    <Select
                        labelId="segment-filter-label"
                        label="Segment"
                        value={selectedSegmentId}
                        onChange={e => setSelectedSegmentId(e.target.value as string)}
                    >
                        <MenuItem value="">All Segments</MenuItem>
                        {segments.map(seg => (
                            <MenuItem key={seg.id} value={seg.id ?? ''}>
                                {seg.code} — {seg.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
            )}

            {/* Empty state */}
            {!loading && !hasData && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No posted entries in the selected period.
                </div>
            )}

            {/* Segment blocks */}
            {!loading && hasData && report!.segments.map((block: SegmentIncomeStatementBlock) => (
                <div key={block.segmentId ?? 'unassigned'} style={{ marginBottom: '32px' }}>
                    {/* Segment header */}
                    <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                            {block.segmentCode ? `${block.segmentCode} — ` : ''}{block.segmentName}
                        </span>
                    </div>

                    {/* Revenue section */}
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                            Revenue
                        </div>
                        {renderLineTable(block.revenueLines)}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                            <tbody>
                                <tr style={{ backgroundColor: '#e8f5e9', fontWeight: 700, borderTop: '2px solid #a5d6a7' }}>
                                    <td style={tableCellStyle} colSpan={2}>Total Revenue</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', color: '#2e7d32' }}>
                                        {formatAmount(block.totalRevenue)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Expenses section */}
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                            Expenses
                        </div>
                        {renderLineTable(block.expenseLines)}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
                            <tbody>
                                <tr style={{ backgroundColor: '#fce4ec', fontWeight: 700, borderTop: '2px solid #f48fb1' }}>
                                    <td style={tableCellStyle} colSpan={2}>Total Expense</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', color: '#c62828' }}>
                                        {formatAmount(block.totalExpense)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Net Profit row */}
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#f3f4f6', borderTop: '2px solid #9e9e9e' }}>
                                <td style={{ ...tableCellStyle, fontWeight: 700 }} colSpan={2}>Net Profit</td>
                                <td style={{
                                    ...tableCellStyle,
                                    textAlign: 'right',
                                    fontWeight: 700,
                                    color: netProfitColor(block.netProfit),
                                }}>
                                    {formatAmount(block.netProfit)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            ))}

            {/* Grand-total footer */}
            {!loading && hasData && (
                <div style={{ marginTop: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ backgroundColor: '#e8f5e9', fontWeight: 700, borderTop: '3px solid #66bb6a' }}>
                                <td style={tableCellStyle} colSpan={2}>Total Revenue</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right', color: '#2e7d32' }}>
                                    {formatAmount(report!.totalRevenue)}
                                </td>
                            </tr>
                            <tr style={{ backgroundColor: '#fce4ec', fontWeight: 700 }}>
                                <td style={tableCellStyle} colSpan={2}>Total Expense</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right', color: '#c62828' }}>
                                    {formatAmount(report!.totalExpense)}
                                </td>
                            </tr>
                            <tr style={{ backgroundColor: '#e8eaf6', fontWeight: 700, borderTop: '2px solid #7986cb' }}>
                                <td style={tableCellStyle} colSpan={2}>Net Profit</td>
                                <td style={{
                                    ...tableCellStyle,
                                    textAlign: 'right',
                                    fontWeight: 700,
                                    color: netProfitColor(report!.netProfit),
                                }}>
                                    {formatAmount(report!.netProfit)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SegmentIncomeStatement;
